import { type FC, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Dialog — centered modal for create/edit flows.
 *
 * Separate from Sheet (side panel). Use Dialog when the action is short
 * (new role, new user, confirmation). Use Sheet when the action is
 * longer and benefits from preserving context (role editor with
 * permission matrix).
 */

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
}

const sizes: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

export const Dialog: FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  children,
  footer,
}) => {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-slate-900/35 backdrop-blur-sm animate-fade-in" />
        <RadixDialog.Content
          // Radix auto-focuses the first focusable element on open. Without
          // preventScroll that focus call scrolls the page's scroll container
          // back to the top. We prevent the default auto-focus and instead
          // focus the dialog itself with preventScroll, preserving page scroll.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement)?.focus({ preventScroll: true });
          }}
          className={cn(
            'fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100vw-2rem)] liquid-glass-strong rounded-glass-xl p-5',
            'max-h-[calc(100dvh-2rem)] overflow-y-auto scrollbar-subtle animate-fade-in',
            sizes[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="min-w-0">
              <RadixDialog.Title className="text-heading font-semibold text-text-primary tracking-tight">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-1 text-caption text-text-tertiary leading-relaxed">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900/5 hover:bg-slate-900/10 shrink-0"
            >
              <X className="w-4 h-4 text-text-secondary" />
            </RadixDialog.Close>
          </div>
          <div className="flex flex-col gap-3">{children}</div>
          {footer && <div className="mt-5 flex items-center justify-end gap-2">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default Dialog;
